/**
 * Guest Swish session recovery for Safari stale/orphaned identities.
 * Does not touch cart lock, removals, logged-in customers, or Save for Later.
 */
(() => {
  const LOG_PREFIX = '[swish-guest-recovery]';
  const COLD_START_KEY = 'smilodox:wk-guest-revalidate';
  const VERIFY_DELAY_MS = 600;
  const REQUEST_TIMEOUT_MS = 4000;
  const INSTALL_POLL_MS = 50;
  const INSTALL_TIMEOUT_MS = 15000;
  const WRAP_FLAG = '__smilodoxGuestRecovery';

  let installed = false;
  let recoveryRan = false;
  let recoveryPromise = null;
  let addChain = Promise.resolve();

  function abbreviateId(value) {
    if (typeof value !== 'string' || !value) {
      return null;
    }
    if (value.length <= 4) {
      return '***';
    }
    return `${value.slice(0, 3)}…${value.slice(-2)}`;
  }

  function log(message, details) {
    if (details) {
      console.info(LOG_PREFIX, message, details);
      return;
    }
    console.info(LOG_PREFIX, message);
  }

  function wait(ms) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  function withTimeout(promise, ms, label) {
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        reject(new Error(`${label} timeout`));
      }, ms);

      promise.then(
        (value) => {
          window.clearTimeout(timer);
          resolve(value);
        },
        (error) => {
          window.clearTimeout(timer);
          reject(error);
        }
      );
    });
  }

  function toVariantId(value) {
    if (value == null || value === '') {
      return null;
    }
    const parsed = typeof value === 'string' ? Number.parseInt(value, 10) : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function isGuest(app) {
    return !app?.isLoggedIn && !app?.customer?.id;
  }

  function getStoredSessionId() {
    try {
      return window.localStorage.getItem('wk_session_id');
    } catch {
      return null;
    }
  }

  function wishlistFromResponse(result) {
    if (!result || typeof result !== 'object') {
      return null;
    }
    if (result.wishlist && typeof result.wishlist === 'object') {
      return result.wishlist;
    }
    if (result.id === 'mine' || result.isMine) {
      return result;
    }
    return null;
  }

  function isOrphanedGuestList(wishlist, variantId) {
    if (variantId == null) {
      return false;
    }
    if (!wishlist || wishlist.publicId) {
      return false;
    }
    if (!Array.isArray(wishlist.items) || wishlist.items.length !== 0) {
      return false;
    }
    if (Number(wishlist.numItems) !== 0) {
      return false;
    }
    return true;
  }

  async function loadMineUncached(app) {
    const loadWishlist = app?.apis?.wishlist?.loadWishlist;
    if (typeof loadWishlist !== 'function') {
      throw new Error('wishlist load API unavailable');
    }

    const result = await withTimeout(
      loadWishlist.call(app.apis.wishlist, {
        wishlistId: 'mine',
        cacheStrategy: 'no-store',
      }),
      REQUEST_TIMEOUT_MS,
      'loadMineUncached'
    );
    const wishlist = wishlistFromResponse(result);
    if (!wishlist) {
      throw new Error('wishlist payload unavailable');
    }
    return wishlist;
  }

  function revalidateInBackground(app) {
    if (typeof app.state?.revalidate !== 'function') {
      return;
    }

    withTimeout(Promise.resolve(app.state.revalidate(true)), REQUEST_TIMEOUT_MS, 'revalidate').catch(() => {
      log('revalidate skipped', { reason: 'timeout' });
    });
  }

  async function runColdStart(app) {
    if (!isGuest(app) || !getStoredSessionId()) {
      return;
    }

    try {
      if (window.sessionStorage.getItem(COLD_START_KEY) === '1') {
        return;
      }
      window.sessionStorage.setItem(COLD_START_KEY, '1');
    } catch {
      return;
    }

    if (typeof app.state?.revalidate !== 'function') {
      return;
    }

    try {
      await withTimeout(Promise.resolve(app.state.revalidate(true)), REQUEST_TIMEOUT_MS, 'cold-start');
      log('cold-start revalidate', { session: abbreviateId(getStoredSessionId()) });
    } catch {
      log('cold-start revalidate skipped', { reason: 'timeout' });
    }
  }

  async function confirmOrphanedIdentity(app, variantId) {
    if (variantId == null) {
      return false;
    }

    await wait(VERIFY_DELAY_MS);
    const first = await loadMineUncached(app);
    const firstOrphaned = isOrphanedGuestList(first, variantId);

    log('server list after add', {
      publicId: Boolean(first.publicId),
      numItems: Number(first.numItems) || 0,
      itemCount: Array.isArray(first.items) ? first.items.length : null,
      session: abbreviateId(getStoredSessionId()),
    });

    if (!firstOrphaned) {
      return false;
    }

    await wait(VERIFY_DELAY_MS);
    const second = await loadMineUncached(app);
    const secondOrphaned = isOrphanedGuestList(second, variantId);

    log('server list second check', {
      publicId: Boolean(second.publicId),
      numItems: Number(second.numItems) || 0,
      itemCount: Array.isArray(second.items) ? second.items.length : null,
      session: abbreviateId(getStoredSessionId()),
    });

    return secondOrphaned;
  }

  async function recoverOrphanedSession(app, variantId) {
    if (!isGuest(app) || recoveryRan || variantId == null || typeof app.clearCustomerSession !== 'function') {
      return false;
    }

    if (recoveryPromise) {
      await recoveryPromise;
      return false;
    }

    recoveryPromise = (async () => {
      let orphaned = false;
      try {
        orphaned = await confirmOrphanedIdentity(app, variantId);
      } catch {
        log('recovery skipped', { reason: 'timeout' });
        return false;
      }

      if (!orphaned) {
        return false;
      }

      const before = abbreviateId(getStoredSessionId());
      recoveryRan = true;
      await app.clearCustomerSession();
      const after = abbreviateId(getStoredSessionId());
      log('recovered orphaned guest session', { from: before, to: after });
      return true;
    })();

    try {
      return await recoveryPromise;
    } finally {
      recoveryPromise = null;
    }
  }

  async function retryAdd(app, original, params, variantId) {
    const retryResult = await original.call(app, params);
    revalidateInBackground(app);
    log('guest add retry success', {
      session: abbreviateId(getStoredSessionId()),
      variant: variantId,
    });
    return retryResult;
  }

  function wrapAddWishlistItem(app) {
    const original = app.addWishlistItem;
    if (typeof original !== 'function' || original[WRAP_FLAG] || app.addWishlistItem[WRAP_FLAG]) {
      return;
    }

    const addWithRecovery = async function addWishlistItemWithGuestRecovery(params, settleAdd) {
      const variantId = toVariantId(params?.variantId);
      let initialError = null;
      let initialResult;

      try {
        initialResult = await original.call(app, params);
        log('guest add initial success', {
          session: abbreviateId(getStoredSessionId()),
          variant: variantId,
        });
        settleAdd.resolve(initialResult);
      } catch (error) {
        initialError = error;
        log('guest add initial error', {
          session: abbreviateId(getStoredSessionId()),
          variant: variantId,
        });
      }

      let recovered = false;
      try {
        recovered = await recoverOrphanedSession(app, variantId);
      } catch {
        log('recovery skipped', { reason: 'timeout' });
      }

      if (recovered) {
        try {
          const retryResult = await retryAdd(app, original, params, variantId);
          if (initialError) {
            settleAdd.resolve(retryResult);
          }
          return;
        } catch (error) {
          log('guest add retry error', {
            session: abbreviateId(getStoredSessionId()),
            variant: variantId,
          });
          if (initialError) {
            settleAdd.reject(error);
            return;
          }
        }
      }

      if (initialError) {
        settleAdd.reject(initialError);
      }
    };

    const wrapped = function queuedGuestAddWishlistItem(params) {
      if (!isGuest(app)) {
        return original.call(app, params);
      }

      let settleAdd;
      const addDone = new Promise((resolve, reject) => {
        settleAdd = { resolve, reject };
      });

      addChain = addChain
        .then(async () => {
          try {
            await addWithRecovery(params, settleAdd);
          } catch (error) {
            settleAdd.reject(error);
          }
        })
        .then(
          () => undefined,
          () => undefined
        );

      return addDone;
    };

    wrapped[WRAP_FLAG] = true;
    app.addWishlistItem = wrapped;
  }

  function install(app) {
    if (installed || !app) {
      return;
    }
    installed = true;
    wrapAddWishlistItem(app);
    void runColdStart(app);
  }

  function waitForWishlistKing() {
    if (window.WishlistKing) {
      install(window.WishlistKing);
      return;
    }

    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (window.WishlistKing) {
        window.clearInterval(timer);
        install(window.WishlistKing);
        return;
      }
      if (Date.now() - startedAt >= INSTALL_TIMEOUT_MS) {
        window.clearInterval(timer);
      }
    }, INSTALL_POLL_MS);
  }

  waitForWishlistKing();
})();
